/**
 * useReferenceTrack Hook
 * Manages reference track upload, Web Audio API analysis, and session injection
 */

import { useState, useCallback } from 'react';
import { ReferenceTrackAnalysis, AnalysisStatus, EnergySegment } from '@/types/referenceTrack.types';
import { analyzeAudioBuffer } from '@utils/audioAnalysis';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

interface UseReferenceTrackReturn {
  analysis: ReferenceTrackAnalysis | null;
  status: AnalysisStatus;
  error: string | null;
  uploadAndAnalyze: (file: File) => Promise<void>;
  analyzeWithoutUpload: (file: File) => Promise<void>;
  clear: () => void;
}

export function useReferenceTrack(): UseReferenceTrackReturn {
  const [analysis, setAnalysis] = useState<ReferenceTrackAnalysis | null>(null);
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const uploadAndAnalyze = useCallback(async (file: File) => {
    setError(null);
    setAnalysis(null);

    try {
      // Step 1: Upload to server
      setStatus('uploading');
      const formData = new FormData();
      formData.append('audioFile', file);

      const uploadRes = await fetchWithCsrf(API_ENDPOINTS.referenceTracks.upload, {
        method: 'POST',
        body: formData
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || `Upload failed (${uploadRes.status})`);
      }

      const uploadData = await uploadRes.json();

      // Step 2: Decode audio with Web Audio API
      setStatus('decoding');
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // Step 3-5: Run analysis
      setStatus('analyzing-bpm');
      // Small delay for UI feedback
      await new Promise(r => setTimeout(r, 50));

      setStatus('analyzing-key');
      await new Promise(r => setTimeout(r, 50));

      setStatus('analyzing-energy');
      const result = analyzeAudioBuffer(audioBuffer, uploadData.originalName || file.name);

      await audioContext.close();

      setAnalysis(result);
      setStatus('complete');
      console.log('🎵 Reference track analysis complete:', result.filename);
    } catch (err: unknown) {
      console.error('🎵 Reference track analysis failed:', err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setStatus('error');
    }
  }, []);

  const analyzeWithoutUpload = useCallback(async (file: File) => {
    setError(null);
    setAnalysis(null);

    try {
      setStatus('decoding');
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      setStatus('analyzing-bpm');
      await new Promise(r => setTimeout(r, 50));

      setStatus('analyzing-key');
      await new Promise(r => setTimeout(r, 50));

      setStatus('analyzing-energy');
      const result = analyzeAudioBuffer(audioBuffer, file.name);

      await audioContext.close();

      setAnalysis(result);
      setStatus('complete');
      console.log('🎵 Reference track analysis complete (local):', result.filename);
    } catch (err: unknown) {
      console.error('🎵 Reference track analysis failed:', err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setStatus('error');
    }
  }, []);

  const clear = useCallback(() => {
    setAnalysis(null);
    setStatus('idle');
    setError(null);
  }, []);

  return { analysis, status, error, uploadAndAnalyze, analyzeWithoutUpload, clear };
}

/**
 * Convert energy segments to EmotionalArcPoints
 * Maps RMS energy (0-100) to emotional intensity and auto-labels
 */
export function energyToEmotionalArc(segments: EnergySegment[]): Array<{ position: number; emotion: string; intensity: number }> {
  const getLabel = (energy: number, prevEnergy: number | null): string => {
    const delta = prevEnergy !== null ? energy - prevEnergy : 0;

    if (energy >= 85) return 'Explosive';
    if (energy >= 70 && delta > 10) return 'Surging';
    if (energy >= 70) return 'Powerful';
    if (energy >= 55 && delta > 10) return 'Building';
    if (energy >= 55) return 'Driving';
    if (energy >= 40 && delta < -10) return 'Retreating';
    if (energy >= 40) return 'Steady';
    if (energy >= 25) return 'Restrained';
    if (energy >= 10) return 'Whisper';
    return 'Silent';
  };

  return segments.map((seg, i) => ({
    position: seg.position,
    emotion: getLabel(seg.energy, i > 0 ? segments[i - 1].energy : null),
    intensity: seg.energy
  }));
}
